/**
 * Resolve persona playbook article IDs for in-app Help.
 */

/**
 * @param {object} manifest
 * @param {string|null|undefined} role
 * @returns {string[]} ordered article ids
 */
export function resolvePlaybookArticleIds(manifest, role) {
  if (!manifest) return [];
  const playbooks = manifest.role_playbooks || {};
  if (role && Array.isArray(playbooks[role]) && playbooks[role].length > 0) {
    return playbooks[role];
  }
  return manifest.fallback_playbook || [];
}

/**
 * @param {object} manifest
 * @param {string} articleId
 * @returns {object|null}
 */
export function findArticleById(manifest, articleId) {
  if (!manifest?.articles || !articleId) return null;
  return manifest.articles.find((a) => a.id === articleId) || null;
}

/**
 * @param {object} manifest
 * @param {string[]} articleIds
 * @returns {object[]}
 */
export function articlesForIds(manifest, articleIds) {
  if (!manifest?.articles || !articleIds?.length) return [];
  const byId = new Map(manifest.articles.map((a) => [a.id, a]));
  return articleIds.map((id) => byId.get(id)).filter(Boolean);
}

/**
 * Group all catalog articles by manifest.groups key.
 * @param {object} manifest
 * @returns {{ groupKey: string, label: string, articles: object[] }[]}
 */
export function groupAllArticles(manifest) {
  if (!manifest?.articles) return [];
  const groups = manifest.groups || {};
  const order = ['orientation', 'workflows', 'reference'];
  const buckets = new Map();

  for (const article of manifest.articles) {
    const key = article.group || 'other';
    if (!buckets.has(key)) {
      buckets.set(key, []);
    }
    buckets.get(key).push(article);
  }

  for (const articles of buckets.values()) {
    articles.sort((a, b) => {
      const aIndex = a.tags?.includes('index') ? 0 : 1;
      const bIndex = b.tags?.includes('index') ? 0 : 1;
      if (aIndex !== bIndex) return aIndex - bIndex;
      return (a.title || '').localeCompare(b.title || '');
    });
  }

  const known = order.filter((k) => buckets.has(k)).map((k) => ({
    groupKey: k,
    label: groups[k] || k,
    articles: buckets.get(k),
  }));

  const extras = [...buckets.keys()]
    .filter((k) => !order.includes(k))
    .map((k) => ({
      groupKey: k,
      label: groups[k] || k,
      articles: buckets.get(k),
    }));

  return [...known, ...extras];
}

/**
 * Group playbook articles by section (same hierarchy as All topics), playbook order within each group.
 * @param {object} manifest
 * @param {string[]} playbookIds
 * @param {string} [query]
 * @returns {{ groupKey: string, label: string, articles: object[] }[]}
 */
export function groupPlaybookArticles(manifest, playbookIds, query = '') {
  if (!manifest?.articles || !playbookIds?.length) return [];

  const articles = filterArticlesByQuery(
    articlesForIds(manifest, playbookIds),
    query
  );
  if (!articles.length) return [];

  const groups = manifest.groups || {};
  const order = ['orientation', 'workflows', 'reference'];
  const playbookIndex = new Map(playbookIds.map((id, i) => [id, i]));
  const buckets = new Map();

  for (const article of articles) {
    const key = article.group || 'other';
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(article);
  }

  for (const list of buckets.values()) {
    list.sort(
      (a, b) => (playbookIndex.get(a.id) ?? 999) - (playbookIndex.get(b.id) ?? 999)
    );
  }

  const known = order.filter((k) => buckets.has(k)).map((k) => ({
    groupKey: k,
    label: groups[k] || k,
    articles: buckets.get(k),
  }));

  const extras = [...buckets.keys()]
    .filter((k) => !order.includes(k))
    .map((k) => ({
      groupKey: k,
      label: groups[k] || k,
      articles: buckets.get(k),
    }));

  return [...known, ...extras];
}

/**
 * Filter articles by title/tags query (v1 title search).
 * @param {object[]} articles
 * @param {string} query
 */
export function filterArticlesByQuery(articles, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return articles;
  return articles.filter((a) => {
    const title = (a.title || '').toLowerCase();
    const tags = (a.tags || []).join(' ').toLowerCase();
    return title.includes(q) || tags.includes(q);
  });
}

/**
 * Resolve a Markdown href to a normalized article path (relative to docs/external).
 * Returns null for external URLs, anchors-only, or non-markdown links.
 * @param {string} href
 * @param {string} currentArticlePath - manifest path of the article being viewed
 * @returns {{ path: string, hash: string|null }|null}
 */
export function resolveHelpMarkdownPath(href, currentArticlePath) {
  if (!href) return null;
  const trimmed = href.trim();
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('mailto:')) {
    return null;
  }

  const hashIndex = trimmed.indexOf('#');
  const pathPart = hashIndex >= 0 ? trimmed.slice(0, hashIndex) : trimmed;
  const hash = hashIndex >= 0 ? trimmed.slice(hashIndex + 1) : null;
  const clean = pathPart.split('?')[0];
  if (!clean || !clean.toLowerCase().endsWith('.md')) {
    return null;
  }

  const currentDir =
    currentArticlePath && currentArticlePath.includes('/')
      ? currentArticlePath.slice(0, currentArticlePath.lastIndexOf('/') + 1)
      : '';

  const combined = `${currentDir}${clean}`;
  const stack = [];
  for (const segment of combined.split('/')) {
    if (!segment || segment === '.') continue;
    if (segment === '..') {
      if (stack.length) stack.pop();
    } else {
      stack.push(segment);
    }
  }

  return { path: stack.join('/'), hash: hash || null };
}

/**
 * @param {object} manifest
 * @param {string} articlePath
 * @returns {object|null}
 */
export function findArticleByPath(manifest, articlePath) {
  if (!manifest?.articles || !articlePath) return null;
  const normalized = articlePath.replace(/\\/g, '/');
  return manifest.articles.find((a) => a.path === normalized) || null;
}

/**
 * Map an in-doc Markdown link to a catalog article when possible.
 * @param {object} manifest
 * @param {string} href
 * @param {string} currentArticlePath
 * @returns {{ article: object, hash: string|null }|null}
 */
export function resolveHelpArticleFromHref(manifest, href, currentArticlePath) {
  const target = resolveHelpLinkTarget(manifest, href, currentArticlePath);
  if (target?.type !== 'article') return null;
  return { article: target.article, hash: target.hash };
}

/**
 * Resolve any help Markdown link to an in-app navigation target.
 * Index/README pages are not shown — they route to Help sections instead.
 * @returns {{ type: 'article', article: object, hash: string|null }|
 *           { type: 'help-section', section: string }|
 *           { type: 'help-home' }|
 *           { type: 'external', href: string }|
 *           { type: 'unavailable' }}
 */
export function resolveHelpLinkTarget(manifest, href, currentArticlePath) {
  if (!href) return { type: 'unavailable' };

  const trimmed = href.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return { type: 'external', href: trimmed };
  }

  const pathOnly = trimmed.split('#')[0].split('?')[0].replace(/\/+$/, '');
  if (pathOnly === 'workflows' || pathOnly.endsWith('/workflows')) {
    return { type: 'help-section', section: 'workflows' };
  }
  if (pathOnly === 'reference' || pathOnly.endsWith('/reference')) {
    return { type: 'help-section', section: 'reference' };
  }

  if (/\.(docx|pdf|zip)$/i.test(pathOnly) || pathOnly.includes('artifact/')) {
    return { type: 'unavailable' };
  }

  const resolved = resolveHelpMarkdownPath(href, currentArticlePath);
  if (!resolved) {
    return { type: 'unavailable' };
  }

  const { path, hash } = resolved;

  if (path === 'workflows/README.md') {
    return { type: 'help-section', section: 'workflows' };
  }
  if (path === 'reference/README.md') {
    return { type: 'help-section', section: 'reference' };
  }
  if (path === 'README.md') {
    return { type: 'help-home' };
  }

  const article = findArticleByPath(manifest, path);
  if (article) {
    return { type: 'article', article, hash };
  }

  return { type: 'unavailable' };
}

/**
 * Swap Word-export footnotes for in-app Help navigation links.
 * Source markdown keeps .docx references for published doc builds.
 * @param {string} markdown
 */
export function replaceDocxReferencesForInApp(markdown) {
  if (!markdown) return '';

  return markdown
    .replace(
      /\*Full operational guide:\s*`DevicePulse_User_Guide\.docx`\s*in the published documentation set\.\*/gi,
      '*Step-by-step guides: [Workflows](workflows/README.md) · [Reference](reference/README.md)*'
    )
    .replace(
      /Detailed guides are in \*\*`DevicePulse_User_Guide\.docx`\*\* \(published documentation set\)\./gi,
      'Detailed guides are in **Help → All topics** — [Workflows](workflows/README.md) and [Reference](reference/README.md).'
    )
    .replace(
      /\*Published as `DevicePulse_Notification_Channels\.docx` in the published documentation set\. Cross-referenced from the DevicePulse User Guide\.\*/gi,
      '*Related: [Alerts & Notifications](workflows/alerts-and-notifications.md) · [Product Overview](overview.md)*'
    )
    .replace(
      /\*\*Full reference:\*\* See the separate \*\*Notification Channels & Delivery Guide\*\* \(`DevicePulse_Notification_Channels\.docx` in the published documentation set\) for/gi,
      '**Full reference:** See **[Notification Channels](../notification-channels.md)** for'
    )
    .replace(
      /- \*\*Notification Channels & Delivery Guide\*\* — full event matrix and role routing \(`DevicePulse_Notification_Channels\.docx`\)/gi,
      '- **[Notification Channels](../notification-channels.md)** — full event matrix and role routing'
    )
    .replace(/ available in-app under /gi, ' available under ')
    .replace(/\(`DevicePulse_[^`]+\.docx` in the published documentation set\)/gi, '')
    .replace(/\(`DevicePulse_[^`]+\.docx`\)/gi, '');
}

/**
 * Strip common inline Markdown for heading slug text.
 * @param {string} text
 */
export function stripHelpInlineMarkdown(text) {
  return (text || '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[(.+?)\]\([^)]*\)/g, '$1')
    .trim();
}

/**
 * @param {string} text
 */
export function slugifyHelpHeading(text) {
  const slug = stripHelpInlineMarkdown(text)
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return slug || 'section';
}

/**
 * Parse h2/h3 headings for in-article jump nav and anchor ids.
 * @param {string} markdown
 * @returns {{ level: number, title: string, id: string }[]}
 */
export function parseHelpHeadings(markdown) {
  if (!markdown) return [];

  const headings = [];
  const slugCounts = new Map();

  for (const line of markdown.split('\n')) {
    const match = line.match(/^(#{2,3})\s+(.+)$/);
    if (!match) continue;

    const level = match[1].length;
    const title = stripHelpInlineMarkdown(match[2].trim());
    if (!title) continue;

    let slug = slugifyHelpHeading(title);
    const seen = slugCounts.get(slug) || 0;
    slugCounts.set(slug, seen + 1);
    if (seen > 0) slug = `${slug}-${seen + 1}`;

    headings.push({ level, title, id: slug });
  }

  return headings;
}

/**
 * Split leading H1 from article markdown for in-app chrome vs body.
 * @param {string} markdown
 * @returns {{ title: string|null, body: string }}
 */
export function parseHelpArticleContent(markdown) {
  if (!markdown) return { title: null, body: '' };

  const lines = markdown.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const match = lines[i].match(/^#\s+(.+)$/);
    if (!match) continue;

    let startIdx = i + 1;
    while (startIdx < lines.length && lines[startIdx].trim() === '') {
      startIdx += 1;
    }

    return {
      title: match[1].trim(),
      body: lines.slice(startIdx).join('\n'),
    };
  }

  return { title: null, body: markdown };
}
